import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./BusinessLoan.css";
import "animate.css/animate.min.css";
import Seo from "../components/Seo";

const businessLoanUses = [
  {
    icon: "bi bi-box-seam",
    title: "Purchase Inventory",
    description:
      "Finance stock and raw-material purchases to maintain operations, meet customer demand and support seasonal growth.",
  },
  {
    icon: "bi bi-people",
    title: "Hire Employees",
    description:
      "Manage recruitment, onboarding, training and initial salary expenses while expanding your workforce.",
  },
  {
    icon: "bi bi-gear",
    title: "Upgrade Machinery",
    description:
      "Purchase eligible machinery, technology or equipment to improve productivity and business capacity.",
  },
  {
    icon: "bi bi-shop",
    title: "Business Expansion",
    description:
      "Open a new branch, increase production capacity or expand into a new service area with suitable funding.",
  },
  {
    icon: "bi bi-cash-stack",
    title: "Working Capital",
    description:
      "Manage regular expenses such as rent, utilities, salaries, supplier payments and operational requirements.",
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Loan Balance Transfer",
    description:
      "Transfer an eligible existing business loan to another lender for potentially improved repayment terms.",
  },
];

const eligibilityItems = [
  {
    image: "/images/Bank Pages/salaried-image-icon.png",
    alt: "Eligible business applicant",
    title: "Applicant Type",
    text:
      "Proprietors, partnerships, private companies, MSMEs and eligible self-employed professionals may apply.",
  },
  {
    image: "/images/Bank Pages/age.png",
    alt: "Business loan applicant age",
    title: "Applicant Age",
    text:
      "Age requirements depend on the lender, applicant profile and requested loan tenure.",
  },
  {
    image: "/images/Bank Pages/3-month-experienced.png",
    alt: "Business experience and continuity",
    title: "Business Vintage",
    text:
      "Lenders generally assess business continuity, industry experience and operational stability.",
  },
  {
    image: "/images/Bank Pages/minimum-income_20000.png",
    alt: "Business financial eligibility",
    title: "Financial Strength",
    text:
      "Eligibility depends on turnover, profitability, banking activity, credit profile and repayment capacity.",
  },
];

const documentGroups = [
  {
    title: "Identity Documents",
    image: "/images/Bank Pages/photo-identy.png",
    items: [
      "PAN Card",
      "Aadhaar Card",
      "Passport",
      "Voter ID",
      "Driving Licence",
      "Recent passport-size photographs",
    ],
  },
  {
    title: "Address Documents",
    image: "/images/Bank Pages/proof-of-residence.png",
    items: [
      "Aadhaar Card",
      "Passport",
      "Driving Licence",
      "Registered rent agreement",
      "Recent utility bill",
      "Bank statement showing current address",
    ],
  },
  {
    title: "Income and Business Documents",
    image: "/images/Bank Pages/income-proof.png",
    items: [
      "Income tax returns",
      "Audited balance sheet",
      "Profit and loss statement",
      "GST returns, where applicable",
      "Business registration certificate",
      "Current and savings account statements",
    ],
  },
  {
    title: "Additional Supporting Documents",
    image: "/images/Bank Pages/other-document.png",
    items: [
      "Partnership deed",
      "Company incorporation documents",
      "Existing loan statements",
      "Existing sanction letters",
      "Equipment quotation or purchase invoice",
      "Property documents for secured funding",
    ],
  },
];

const processSteps = [
  {
    title: "Share Your Requirement",
    text:
      "Tell us the required loan amount, business purpose, company profile and preferred repayment period.",
  },
  {
    title: "Initial Profile Review",
    text:
      "Our team reviews your turnover, banking activity, credit profile, obligations and basic eligibility.",
  },
  {
    title: "Compare Lending Options",
    text:
      "Compare suitable bank and NBFC options based on indicative rates, tenure, fees and eligibility.",
  },
  {
    title: "Submit the Application",
    text:
      "Complete the application and submit the necessary personal, financial and business documents.",
  },
  {
    title: "Lender Assessment",
    text:
      "The lender verifies your documents, banking behaviour, credit history, business stability and repayment capacity.",
  },
  {
    title: "Sanction and Disbursement",
    text:
      "After approval and completion of all conditions, the sanctioned loan amount is disbursed.",
  },
];

const bankRates = [
  {
    bank: "Axis Bank",
    url: "https://www.axisbank.com/retail/loans/business-loan",
    rate: "10.75%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "As per product",
    fee: "As per bank policy",
  },
  {
    bank: "HDFC Bank",
    url:
      "https://www.hdfcbank.com/personal/borrow/popular-loans/business-loan",
    rate: "10.75%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "As per product",
    fee: "As per bank policy",
  },
  {
    bank: "IndusInd Bank",
    url:
      "https://induseasycredit.indusind.com/STBL/small-ticket/getting-started",
    rate: "13.00%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 5 years",
    fee: "As per bank policy",
  },
  {
    bank: "DBS Bank",
    url:
      "https://www.dbs.com/in/sme/financing/digibusinessloans.page",
    rate: "13.00%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "As per product",
    fee: "As per bank policy",
  },
  {
    bank: "Standard Chartered Bank",
    url:
      "https://www.sc.com/in/business/business-expansion/business-instalment-loan/",
    rate: "15.00%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "As per product",
    fee: "As per bank policy",
  },
  {
    bank: "Poonawalla Fincorp",
    url: "https://poonawallafincorp.com/business-loan",
    rate: "15.00%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "As per product",
    fee: "As per lender policy",
  },
  {
    bank: "Deutsche Bank",
    url:
      "https://www.deutschebank.co.in/en/loans/unsecured-business-loan.html",
    rate: "16.00%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "As per product",
    fee: "As per bank policy",
  },
];

const faqs = [
  {
    question: "What is a business loan?",
    answer:
      "A business loan is financing provided to eligible entrepreneurs, professionals and enterprises for requirements such as working capital, machinery purchase, inventory, expansion or other approved business purposes.",
  },
  {
    question: "Who can apply for a business loan?",
    answer:
      "Eligible applicants may include proprietors, partnership firms, private limited companies, MSMEs and self-employed professionals. Final eligibility depends on lender policy.",
  },
  {
    question: "Which documents are generally required?",
    answer:
      "Common requirements include KYC documents, business registration proof, bank statements, income tax returns, financial statements, GST returns and additional lender-specific documents.",
  },
  {
    question: "How much business loan can I receive?",
    answer:
      "The eligible amount depends on business turnover, profitability, banking activity, credit history, existing obligations, security offered and the lender’s internal policy.",
  },
  {
    question: "Is collateral required?",
    answer:
      "Both secured and unsecured business-loan options may be available. Secured loans require acceptable collateral, while unsecured loans depend more heavily on financial strength and credit history.",
  },
  {
    question: "What affects the business-loan interest rate?",
    answer:
      "The rate may depend on the business profile, industry, loan amount, turnover, credit score, banking history, repayment capacity, tenure and security offered.",
  },
  {
    question: "How long does business-loan approval take?",
    answer:
      "Processing time depends on document completeness, lender verification, credit assessment and any required legal or technical checks.",
  },
  {
    question: "Can I submit my application online?",
    answer:
      "Yes. You can submit your enquiry online and receive assistance with document collection, lender comparison and application processing.",
  },
  {
    question: "Can I transfer my existing business loan?",
    answer:
      "An eligible business loan may be transferred to another lender for potentially better rates, EMI or tenure, subject to assessment and transfer charges.",
  },
  {
    question: "Can I prepay or foreclose the loan?",
    answer:
      "Many lenders permit part-prepayment or foreclosure, subject to applicable terms, lock-in periods and charges mentioned in the sanction letter.",
  },
];

function BusinessLoan() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Seo
        title="Business Loan in Pune | MONAYRA (OPC) PRIVATE LIMITED"
        description="Explore business loan options in Pune with MONAYRA (OPC) PRIVATE LIMITED. Get assistance with eligibility, lender comparison, documentation and loan processing."
        keywords="business loan in Pune, MSME loan, working capital loan, business expansion loan, apply business loan online"
        canonical="https://www.mortbuzz.in/business-loan"
      />

      <main className="monayra-business-loan-page">
        <section className="business-loan-hero">
          <div className="business-loan-hero-overlay"></div>

          <div className="business-loan-hero-container">
            <span className="business-loan-hero-eyebrow animate__animated animate__fadeInDown">
              Business Finance Solutions
            </span>

            <h1 className="animate__animated animate__fadeInUp">
              Fund your next stage of
              <span> business growth</span>
            </h1>

            <p className="animate__animated animate__fadeInUp animate__delay-1s">
              Compare suitable business-loan options and receive professional
              assistance for eligibility, documentation, lender coordination
              and disbursement.
            </p>

            <div className="business-loan-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
              <Link to="/apply-now" className="business-loan-primary-btn">
                Apply for Business Loan
                <i className="bi bi-arrow-right"></i>
              </Link>

              <a
                href="#business-loan-rates"
                className="business-loan-secondary-btn"
              >
                <i className="bi bi-bar-chart"></i>
                Compare Indicative Rates
              </a>
            </div>

            <div className="business-loan-hero-features">
              <div>
                <i className="bi bi-bank"></i>
                Bank and NBFC options
              </div>

              <div>
                <i className="bi bi-file-earmark-check"></i>
                Documentation assistance
              </div>

              <div>
                <i className="bi bi-briefcase"></i>
                MSME and business support
              </div>
            </div>
          </div>
        </section>

        <section className="business-loan-intro-section">
          <div className="business-loan-page-container business-loan-intro-grid">
            <div className="business-loan-intro-image">
              <img
                src="/images/FrontPage/bussineloanimage.png"
                alt="Business owner discussing finance options"
              />

              <div className="business-loan-image-overlay"></div>

              <div className="business-loan-rate-card">
                <span>Indicative starting rate</span>
                <strong>9.00%*</strong>
                <small>Subject to lender policy</small>
              </div>
            </div>

            <div className="business-loan-intro-content">
              <span className="business-loan-section-eyebrow">
                Why Choose MONAYRA?
              </span>

              <h2>
                Business financing support built around
                <span> your goals</span>
              </h2>

              <p>
                MONAYRA (OPC) PRIVATE LIMITED helps eligible businesses
                understand and compare suitable funding options from selected
                banks and financial institutions.
              </p>

              <p>
                Our team supports applicants with eligibility review,
                documentation, lender coordination, application tracking and
                disbursement assistance.
              </p>

              <div className="business-loan-benefits">
                {[
                  "Indicative rates starting from 9.00%*",
                  "Secured and unsecured options",
                  "Support for MSMEs and professionals",
                  "Working capital and expansion finance",
                  "Multiple bank and NBFC options",
                  "Assistance until disbursement",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="business-loan-inline-btn"
              >
                Check Your Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="business-loan-uses-section">
          <div className="business-loan-page-container">
            <div className="business-loan-section-header">
              <span className="business-loan-section-eyebrow">
                Flexible Business Funding
              </span>

              <h2>
                Common uses of a
                <span> business loan</span>
              </h2>

              <p>
                Use suitable business finance to strengthen operations,
                manage cash flow and support sustainable growth.
              </p>
            </div>

            <div className="business-loan-uses-grid">
              {businessLoanUses.map((item) => (
                <article
                  className="business-loan-use-card"
                  key={item.title}
                >
                  <span className="business-loan-use-icon">
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="business-loan-eligibility-section">
          <div className="business-loan-page-container">
            <div className="business-loan-section-header">
              <span className="business-loan-section-eyebrow">
                Basic Eligibility
              </span>

              <h2>
                Business-loan
                <span> eligibility factors</span>
              </h2>

              <p>
                The lender evaluates the business profile, financial
                performance, credit history and repayment capacity.
              </p>
            </div>

            <div className="business-loan-eligibility-grid">
              {eligibilityItems.map((item) => (
                <article
                  className="business-loan-eligibility-card"
                  key={item.title}
                >
                  <div className="business-loan-eligibility-image">
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                    />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="business-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final eligibility depends on the selected lender’s policies
                and complete assessment of the applicant and business.
              </span>
            </div>
          </div>
        </section>

        <section className="business-loan-cta-section">
          <div className="business-loan-page-container">
            <div className="business-loan-cta-card">
              <div>
                <span>Finance Your Growth</span>

                <h2>Ready to move your business forward?</h2>

                <p>
                  Submit your requirement and explore suitable business-loan
                  options with our assistance.
                </p>
              </div>

              <Link to="/apply-now" className="business-loan-primary-btn">
                Apply Now
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="business-loan-documents-section">
          <div className="business-loan-page-container">
            <div className="business-loan-section-header">
              <span className="business-loan-section-eyebrow">
                Document Checklist
              </span>

              <h2>
                Documents generally required for a
                <span> business loan</span>
              </h2>

              <p>
                The final document list depends on the applicant type,
                business structure, loan product and selected lender.
              </p>
            </div>

            <div className="business-loan-documents-grid">
              {documentGroups.map((group) => (
                <article
                  className="business-loan-document-card"
                  key={group.title}
                >
                  <div className="business-loan-document-heading">
                    <span>
                      <img
                        src={group.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    </span>

                    <h3>{group.title}</h3>
                  </div>

                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <i className="bi bi-check-circle"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="business-loan-process-section">
          <div className="business-loan-page-container">
            <div className="business-loan-section-header">
              <span className="business-loan-section-eyebrow">
                Application Journey
              </span>

              <h2>
                A simple business-loan
                <span> application process</span>
              </h2>
            </div>

            <div className="business-loan-process-grid">
              {processSteps.map((step, index) => (
                <article
                  className="business-loan-process-card"
                  key={step.title}
                >
                  <span className="business-loan-process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="business-loan-rates"
          className="business-loan-rates-section"
        >
          <div className="business-loan-page-container">
            <div className="business-loan-section-header">
              <span className="business-loan-section-eyebrow">
                Indicative Comparison
              </span>

              <h2>
                Business-loan
                <span> interest-rate overview</span>
              </h2>

              <p>
                Rates, amounts, tenure and charges are indicative and must be
                confirmed with the selected lender.
              </p>
            </div>

            <div className="business-loan-table-wrapper">
              <table className="business-loan-rate-table">
                <thead>
                  <tr>
                    <th>Lender</th>
                    <th>Starting Rate</th>
                    <th>Indicative Amount</th>
                    <th>Tenure</th>
                    <th>Processing Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {bankRates.map((bank) => (
                    <tr key={bank.bank}>
                      <td>
                        <a
                          href={bank.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bank.bank}
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </td>

                      <td>
                        <strong>{bank.rate}</strong>
                      </td>

                      <td>{bank.amount}</td>
                      <td>{bank.tenure}</td>
                      <td>{bank.fee}</td>

                      <td>
                        <Link
                          to="/apply-now"
                          className="business-loan-table-btn"
                        >
                          Apply
                          <i className="bi bi-arrow-right"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="business-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final terms depend on lender policy, business assessment,
                credit history and document verification.
              </span>
            </div>
          </div>
        </section>

        <section className="business-loan-fee-section">
          <div className="business-loan-page-container business-loan-fee-grid">
            <div className="business-loan-fee-image">
              <img
                src="/images/FrontPage/loan-dealing.jpg"
                alt="Business-loan processing discussion"
                loading="lazy"
              />
            </div>

            <div className="business-loan-fee-content">
              <span className="business-loan-section-eyebrow">
                Loan Charges
              </span>

              <h2>
                Understand the
                <span> processing fee</span>
              </h2>

              <p>
                Lenders may charge processing fees to assess the application,
                verify business documents and complete credit evaluation.
              </p>

              <p>
                The exact amount varies according to the lender, loan amount,
                applicant profile, product type and available offer.
                Applicable taxes may also be charged.
              </p>

              <div className="business-loan-fee-highlight">
                <i className="bi bi-receipt"></i>

                <div>
                  <strong>Compare the total borrowing cost</strong>

                  <span>
                    Review the interest rate, processing fee, insurance,
                    documentation and other applicable lender charges.
                  </span>
                </div>
              </div>

              <Link
                to="/apply-now"
                className="business-loan-inline-btn"
              >
                Get Suitable Offers
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="business-loan-transfer-section">
          <div className="business-loan-page-container business-loan-transfer-grid">
            <div className="business-loan-transfer-content">
              <span className="business-loan-section-eyebrow">
                Existing Loan Transfer
              </span>

              <h2>
                Explore a business-loan
                <span> balance transfer</span>
              </h2>

              <p>
                A balance transfer moves an eligible outstanding business loan
                from the current lender to another lender offering potentially
                better repayment terms.
              </p>

              <div className="business-loan-benefits">
                {[
                  "Potentially lower interest rate",
                  "Possible reduction in EMI",
                  "More suitable repayment tenure",
                  "Top-up funding subject to eligibility",
                  "Improved repayment structure",
                  "Transfer-cost comparison assistance",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="business-loan-inline-btn"
              >
                Check Transfer Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="business-loan-transfer-image">
              <img
                src="/images/FrontPage/a-soft-atmospheric-portrait-of-a-young-i_su4i5o5YT5qmZIIf5VboDw_NyivWLwnSFKpRHSgNB8djA.jpeg"
                alt="Business owner reviewing finance options"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="business-loan-faq-section">
          <div className="business-loan-page-container">
            <div className="business-loan-section-header">
              <span className="business-loan-section-eyebrow">
                Common Questions
              </span>

              <h2>
                Business-loan
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="business-loan-faq-layout">
              <div className="business-loan-faq-list">
                {faqs.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`business-loan-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="business-loan-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="business-loan-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="business-loan-help-card">
                <span className="business-loan-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="business-loan-help-eyebrow">
                  Need Assistance?
                </span>

                <h3>Speak with our business-loan team</h3>

                <p>
                  Share your business type, required loan amount, turnover and
                  funding purpose to understand suitable next steps.
                </p>

                <Link
                  to="/contact"
                  className="business-loan-primary-btn"
                >
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/apply-now"
                  className="business-loan-help-secondary"
                >
                  <i className="bi bi-file-earmark-text"></i>
                  Apply Online
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="business-loan-disclaimer">
          <div className="business-loan-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              *Displayed rates, loan amounts and product details are
              indicative. Final approval, interest rate, tenure, fees and
              other conditions are determined by the selected lender after
              complete assessment. MONAYRA (OPC) PRIVATE LIMITED provides
              loan assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default BusinessLoan;